<template>
  <div class="log-con">
    <LoginHeader />
    <div class="social-login-wrap">
      <button
        v-if="isIOS"
        type="button"
        class="social-btn l-btn apple"
        :disabled="isLoggingIn"
        @click="onClickLoginApple"
      >
        <IconLoading v-if="isLoggingInApple" />
        <template v-else>
          <img :src="getAssetImageUrl('common/icons/apple-logo.svg')" alt="" />
          <span>{{ t('continue_with_social', { social: 'Apple' }) }}</span>
        </template>
      </button>
      <button type="button" class="social-btn l-btn facebook" :disabled="isLoggingIn" @click="onClickLoginFacebook">
        <IconLoading v-if="isLoggingInFacebook" />
        <template v-else>
          <img :src="getAssetImageUrl('common/icons/facebook-logo.svg')" alt="" />
          <span>{{ t('continue_with_social', { social: 'Facebook' }) }}</span>
        </template>
      </button>
      <button type="button" class="social-btn l-btn google" :disabled="isLoggingIn" @click="onClickLoginGoogle">
        <IconLoading v-if="isLoggingInGoogle" />
        <template v-else>
          <img :src="getAssetImageUrl('common/icons/google-logo.svg')" alt="" />
          <span>{{ t('continue_with_social', { social: 'Google' }) }}</span>
        </template>
      </button>
    </div>
    <div class="or">
      <span>{{ t('or') }}</span>
    </div>

    <button class="gray-btn l-btn" :disabled="isLoggingIn" @click="goToLoginSub">
      <span>{{ t('ss_login_as_sub') }}</span>
    </button>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import type { AxiosError } from 'axios';

import { loginSocialMobile } from '@/api/apiService';
import api from '@/api/index';
import LoginHeader from '@/components/domains/mobile/log/LoginHeader.vue';
import IconLoading from '@/components/icons/IconLoading.vue';
import { useMobileInit } from '@/composables/domains/mobile/useMobileInit';
import { useUserStore } from '@/stores/user';
import { logoutSocialMobile } from '@/utils/authService';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { secureStorageSet, navigateInApp } from '@/utils/native/nativeBridge';

const { t } = useI18n();
const { setUserLanguage } = useMobileInit();
const userStore = useUserStore();
const router = useRouter();

const isLoggingIn = ref(false);
const isLoggingInGoogle = ref(false);
const isLoggingInFacebook = ref(false);
const isLoggingInApple = ref(false);

const isIOS = Capacitor.getPlatform() === 'ios';
const isLocalTest = import.meta.env.VITE_IS_MOBILE_LOCAL === 'true';
const baseURL = api.defaults.baseURL ?? '';

const onClickLoginGoogle = async () => {
  if (isLoggingIn.value) return;
  isLoggingIn.value = true;
  isLoggingInGoogle.value = true;

  try {
    await logoutSocialMobile('google');
  } catch (error) {
    console.debug(error);
  }

  try {
    const resLoginSocial = await SocialLogin.login({
      provider: 'google',
      options: { prompt: 'select_account' },
    });

    if (resLoginSocial.result.responseType !== 'online') {
      throw new Error('Google login is offline mode');
    }

    const { idToken, profile } = resLoginSocial.result;

    if (!idToken || !profile || !profile.email) {
      throw new Error('Missing accessToken or profile from Google login');
    }

    await loginToSconChat({
      provider: 'THIRD_PARTY_GOOGLE',
      credential: idToken,
      id: profile.email,
      email: profile.email ?? '',
    });
  } catch (error) {
    console.error('GOOGLE ERROR', error);
  } finally {
    isLoggingIn.value = false;
    isLoggingInGoogle.value = false;
  }
};

const onClickLoginFacebook = async () => {
  if (isLoggingIn.value) return;
  isLoggingIn.value = true;
  isLoggingInFacebook.value = true;

  try {
    await logoutSocialMobile('facebook');
  } catch (error) {
    console.debug(error);
  }

  try {
    const resLoginSocial = await SocialLogin.login({
      provider: 'facebook',
      options: {
        permissions: ['email', 'public_profile'],
        limitedLogin: false,
      },
    });

    const { accessToken, profile, idToken } = resLoginSocial.result;

    if (!accessToken || !profile || !profile.userID) {
      throw new Error('Missing accessToken or profile from Facebook login');
    }
    await loginToSconChat({
      provider: 'THIRD_PARTY_META',
      credential: idToken || accessToken?.token || '',
      id: profile.userID,
      email: profile.email ?? '',
    });
  } catch (error) {
    console.error('FB ERROR', error);
  } finally {
    isLoggingIn.value = false;
    isLoggingInFacebook.value = false;
  }
};

const onClickLoginApple = async () => {
  if (isLoggingIn.value) return;
  isLoggingIn.value = true;
  isLoggingInApple.value = true;

  try {
    await logoutSocialMobile('apple');
  } catch (error) {
    console.debug(error);
  }

  try {
    const resLoginSocial = await SocialLogin.login({
      provider: 'apple',
      options: {},
    });

    const { idToken, profile } = resLoginSocial.result;

    if (!idToken || !profile || !profile.email) {
      throw new Error('Missing accessToken or profile from Apple login');
    }
    await loginToSconChat({
      provider: 'THIRD_PARTY_APPLE',
      credential: idToken,
      id: profile.user,
      email: profile.email ?? '',
      name: profile.givenName ?? '', // TODO 애플은 이름을 매번 주는게 아니라서, 회원가입할 때 이름 입력 받는 로직 필요할듯
    });
  } catch (error) {
    console.error('APPLE ERROR', error);
  } finally {
    isLoggingIn.value = false;
    isLoggingInApple.value = false;
  }
};

interface PayloadLogin {
  provider: 'THIRD_PARTY_GOOGLE' | 'THIRD_PARTY_META' | 'THIRD_PARTY_APPLE';
  credential: string;
  id: string;
  email: string;
  name?: string;
}

const loginToSconChat = async (payloadLogin: PayloadLogin) => {
  try {
    await secureStorageSet({ key: 'authToken', value: payloadLogin.credential });

    const resLoginScon = await loginSocialMobile(payloadLogin);
    if (!resLoginScon.success) return;

    const { accessToken, refreshToken } = resLoginScon.data;

    await userStore.setLoginInfo({
      token: accessToken,
      loginType: 'SELF_SERVICE',
      partnerCode: null,
      refreshToken,
    });
    await userStore.fetchUserInfo();

    await secureStorageSet({
      key: 'authSession',
      value: JSON.stringify({ refreshToken, provider: payloadLogin.provider }),
    });

    goToDashboard();
  } catch (error: unknown) {
    const err = error as AxiosError<{ code?: number; errorCode?: number; message?: string }>;
    const errorCode = err.errorCode ?? err.response?.data?.code ?? err.response?.data?.errorCode;

    console.error('SCON ERROR', error, `ERROR CODE: ${errorCode}`);

    // TODO 502 에러 등 네트워크 이슈 모달
    if (errorCode === 1004) {
      goToSignup(payloadLogin);
      return;
    } else {
      alert(t('error_server_or_network'));
    }
  }
};

const goToDashboard = () => {
  if (isLocalTest) {
    router.push({ name: 'app-main-view' });
    return;
  }
  navigateInApp(`${baseURL}/app/main`);
};

const goToSignup = async (payloadLogin: PayloadLogin) => {
  const { id: userId, provider, credential: authToken, email } = payloadLogin;
  const oAuth = provider.split('_').at(-1)?.toLowerCase();

  await secureStorageSet({
    key: 'signupInfo',
    value: JSON.stringify({
      oAuth,
      userId,
      accessToken: authToken,
      email,
      ...(oAuth === 'apple' && { nickname: payloadLogin.name }),
    }),
  });

  if (isLocalTest) {
    router.push({ name: 'app-signup' });
    return;
  }
  navigateInApp(`${baseURL}/app/signup`);
};

const goToLoginSub = () => {
  router.push({ name: 'native-adduse' });
};

const initSocialLogin = async () => {
  try {
    await SocialLogin.initialize({
      google: {
        iOSClientId: import.meta.env.VITE_CLIENT_ID_GOOGLE_IOS,
        webClientId: import.meta.env.VITE_CLIENT_ID_GOOGLE_WEB,
        mode: 'online',
      },
      facebook: {
        appId: import.meta.env.VITE_APP_ID_FACEBOOK,
        clientToken: import.meta.env.VITE_CLIENT_ID_FACEBOOK,
      },
      ...(isIOS ? { apple: {} } : {}),
    });
  } catch (error) {
    console.error('SOCIAL LOGIN INIT ERROR', error);
  }
};

onBeforeMount(async () => {
  await setUserLanguage();
});

onMounted(async () => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  await initSocialLogin();
});
</script>
<style scoped lang="scss">
.log-con {
  padding: $app-padding;
  .social-login-wrap {
    .social-btn {
      width: 100%;
      border-radius: $size-s;
      margin-bottom: $size-s;
      &:last-child {
        margin-bottom: 0;
      }
      &.apple {
        background: #000;
        span {
          color: #fff;
        }
      }
      &.facebook {
        background: #0866ff;
        span {
          color: #fff;
        }
      }
      &.google {
        background: #fff;
        border: 1px solid $gray50;
        span {
          color: $color-gray-900;
        }
      }
    }
  }
  .or {
    position: relative;
    text-align: center;
    &::before {
      content: '';
      position: absolute;
      display: block;
      width: 100%;
      height: 1px;
      background: $color-gray-30;
      top: 50%;
      transform: translateY(-50%);
    }
    span {
      position: relative;
      background: #fff;
      z-index: 1;
      display: inline-block;
      padding: 1rem 0.5rem;
      color: $color-gray-80;
      font-size: $font-size-xxs;
    }
  }
  .gray-btn {
    text-decoration: none;
    span {
      color: $color-gray-900;
    }
  }
}
</style>
